import React, { useMemo } from "react";

interface AgentProfilePictureUploaderProps {
  currentPictureUrl?: string;
  newPictureFile: File | null;
  onFileChange: (file: File | null) => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const AgentProfilePictureUploader: React.FC<
  AgentProfilePictureUploaderProps
> = ({ currentPictureUrl, newPictureFile, onFileChange }) => {
  const previewUrl = useMemo(() => {
    if (newPictureFile) {
      return URL.createObjectURL(newPictureFile);
    }

    if (!currentPictureUrl) {
      return null;
    }

    return currentPictureUrl.startsWith("http")
      ? currentPictureUrl
      : `https://d37wor0wdsqiqn.cloudfront.net/${currentPictureUrl}`;
  }, [currentPictureUrl, newPictureFile]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      alert("File size must be less than 5MB.");
      return;
    }

    onFileChange(file);
  };

  return (
    <div className="mb-6">
      <label className="block text-gray-700 font-medium mb-2">
        Agent Profile Picture
      </label>

      <div className="flex items-center gap-4">
        <div className="relative">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Agent profile"
              className="w-20 h-20 rounded-full object-cover border-2 border-gray-300"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-surface-ui flex items-center justify-center border-2 border-gray-300 text-gray-400 text-sm">
              No Image
            </div>
          )}
          {newPictureFile && (
            <button
              type="button"
              onClick={() => onFileChange(null)}
              className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-700 transition-colors"
            >
              ×
            </button>
          )}
        </div>

        <div>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            id="agent-profile-picture"
            className="hidden"
          />
          <label
            htmlFor="agent-profile-picture"
            className="inline-block px-4 py-2 bg-white border border-gray-300 rounded cursor-pointer hover:bg-gray-50 transition-colors text-gray-700"
          >
            {newPictureFile || currentPictureUrl ? "Change picture" : "Upload picture"}
          </label>
          <p className="text-xs text-gray-500 mt-2">
            JPG, PNG or GIF, up to 5MB. Saved when you update the agent.
          </p>
        </div>
      </div>

    </div>
  );
};

export default AgentProfilePictureUploader;

